import { type AdvancedFilterState } from '../types';

import {
  ENTITLEMENT_LIST_SORT_ACTIONS,
  type EntitlementListSortFieldState,
} from './entitlements';
import { ADVANCED_FILTERS_METHODS } from './global';

export const INHERENT_RISKS_INI_CALL: Record<string, string> = {
  FILTERS: 'INHERENT_RISKS_FILTERS',
  COLUMNS: 'INHERENT_RISKS_COLUMNS',
};

const INHERENT_RISKS_INITIAL_FILTERS: AdvancedFilterState[] = [
  {
    label: 'Risk Name',
    value: 'riskName',
    category: 'Risk',
    method: 'search',
    isChecked: false,
    isDisabled: false,
  },
  {
    label: 'Risk Status',
    value: 'status',
    category: 'Risk',
    method: 'search',
    isChecked: false,
    isDisabled: false,
  },
  {
    label: 'Priority',
    value: 'priority',
    category: 'Risk',
    method: 'search',
    isChecked: false,
    isDisabled: false,
  },
  {
    label: 'Risk Type',
    value: 'riskType',
    category: 'Risk',
    method: 'search',
    isChecked: false,
    isDisabled: false,
  },
  {
    label: 'Ruleset',
    value: 'ruleset',
    method: 'search',
    isChecked: false,
    isDisabled: false,
  },
  {
    label: 'Function',
    value: 'functionName',
    method: 'search',
    isChecked: false,
    isDisabled: false,
  },
  {
    label: 'Business Process',
    value: 'businessProcess',
    method: 'search',
    isChecked: false,
    isDisabled: false,
  },
];

export default INHERENT_RISKS_INITIAL_FILTERS;

export const ENTITLEMENT_RISK_LIST_AD_FILTER_CAT: Record<string, string> = {
  RISK: 'Risk',
  RULESET: 'Ruleset',
  FUNCTIONS: 'Functions',
} as const;

export const ENTITLEMENT_RISK_LIST_AD_FILTER: Record<
  string,
  AdvancedFilterState
> = {
  RISKNAME: {
    label: 'Risk',
    value: 'riskName',
    category: ENTITLEMENT_RISK_LIST_AD_FILTER_CAT.RISK,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  STATUS: {
    label: 'Risk Status',
    value: 'status',
    category: ENTITLEMENT_RISK_LIST_AD_FILTER_CAT.RISK,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      {
        label: 'Active',
        value: 'ACTIVE',
        isChecked: false,
      },
      {
        label: 'Inactive',
        value: 'INACTIVE',
        isChecked: false,
      },
    ],
  },
  PRIORITY: {
    label: 'Risk Priority',
    value: 'priority',
    category: ENTITLEMENT_RISK_LIST_AD_FILTER_CAT.RISK,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      {
        label: 'Critical',
        value: 'CRITICAL',
        isChecked: false,
      },
      {
        label: 'Low',
        value: 'LOW',
        isChecked: false,
      },
      {
        label: 'Medium',
        value: 'MEDIUM',
        isChecked: false,
      },
      {
        label: 'High',
        value: 'HIGH',
        isChecked: false,
      },
    ],
  },
  RISKTYPE: {
    label: 'Risk Type',
    value: 'riskType',
    category: ENTITLEMENT_RISK_LIST_AD_FILTER_CAT.RISK,
    method: ADVANCED_FILTERS_METHODS.FIXED,
    options: [
      {
        label: 'SOD',
        value: 'SOD',
        isChecked: false,
      },
      {
        label: 'Critical Access',
        value: 'CRITICAL_ACCESS',
        isChecked: false,
      },
      {
        label: 'None',
        value: 'NONE',
        isChecked: false,
      },
    ],
  },
  RULESET: {
    label: 'Ruleset',
    value: 'ruleset',
    category: ENTITLEMENT_RISK_LIST_AD_FILTER_CAT.RULESET,
    method: ADVANCED_FILTERS_METHODS.API_WITH_OPTIONS,
  },
  FUNCTION: {
    label: 'Functions',
    value: 'functionName',
    category: ENTITLEMENT_RISK_LIST_AD_FILTER_CAT.FUNCTIONS,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
  BUSINESSPROCESS: {
    label: 'Business Process',
    value: 'businessProcess',
    category: ENTITLEMENT_RISK_LIST_AD_FILTER_CAT.RISK,
    method: ADVANCED_FILTERS_METHODS.SEARCH,
  },
} as const;

export const DEFAULT_RISK_FILTERS: string[] = [
  ENTITLEMENT_RISK_LIST_AD_FILTER.RISKNAME.value,
  ENTITLEMENT_RISK_LIST_AD_FILTER.STATUS.value,
  ENTITLEMENT_RISK_LIST_AD_FILTER.PRIORITY.value,
  ENTITLEMENT_RISK_LIST_AD_FILTER.RISKTYPE.value,
  ENTITLEMENT_RISK_LIST_AD_FILTER.RULESET.value,
  ENTITLEMENT_RISK_LIST_AD_FILTER.FUNCTION.value,
  ENTITLEMENT_RISK_LIST_AD_FILTER.BUSINESSPROCESS.value,
] as const;

export const ENTITLEMENT_RISK_LIST_INITIAL_FILTERS: AdvancedFilterState[] =
  Object.values(ENTITLEMENT_RISK_LIST_AD_FILTER).map((filter) => {
    const isDefaultFilter = DEFAULT_RISK_FILTERS.includes(filter.value);

    return {
      ...filter,
      isDisabled: false,
      isChecked: isDefaultFilter,
    };
  });

export const ENTITLEMENT_RISK_LIST_SORT_FIELDS: Record<
  string,
  EntitlementListSortFieldState
> = {
  RISKNAME: {
    label: 'Risk Name',
    value: 'riskName',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  RULESET: {
    label: 'Ruleset',
    value: 'ruleset',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  BUSINESSPROCESS: {
    label: 'Business Process',
    value: 'businessProcess',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  FUNCTION1: {
    label: 'Function Name 1',
    value: 'function1',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  FUNCTION2: {
    label: 'Function Name 2',
    value: 'function2',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  FUNCTION3: {
    label: 'Function Name 3',
    value: 'function3',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  FUNCTION4: {
    label: 'Function Name 4',
    value: 'function4',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
  FUNCTION5: {
    label: 'Function Name 5',
    value: 'function5',
    options: [
      ENTITLEMENT_LIST_SORT_ACTIONS.ATOZ.value,
      ENTITLEMENT_LIST_SORT_ACTIONS.ZTOA.value,
    ],
  },
};

export const DEFAULT_RISK_COLUMNS = [
  'businessProcess',
  'ruleset',
  'riskName',
  'function1',
  'function2',
  'function3',
  'function4',
  'function5',
];
